import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from './ui/dialog';
import { Mail, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: 'subject', label: 'Email subject', type: 'input' },
  { key: 'welcome_line', label: 'Welcome line (orange headline)', type: 'input' },
  { key: 'intro', label: 'Intro paragraphs (separate paragraphs with a blank line)', type: 'textarea', rows: 6 },
  { key: 'temp_password', label: 'Temporary password shown in the letter', type: 'input' },
  { key: 'password_note', label: 'Note under the password', type: 'textarea', rows: 3 },
  { key: 'portal_button', label: 'Orange button label', type: 'input' },
  { key: 'training_button', label: 'Blue button label', type: 'input' },
  { key: 'closing_line', label: 'Closing line', type: 'input' },
];

export const WelcomeLetterEditor = ({ open, onOpenChange }) => {
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/welcome-template`)
      .then((res) => setTpl(res.data.template))
      .catch(() => toast.error('Could not load the welcome letter'));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/welcome-template`, tpl);
      toast.success('Welcome letter saved — all future welcome emails will use it');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="welcome-letter-editor">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#34657f]" />
            Edit Welcome Letter
          </DialogTitle>
          <DialogDescription>
            Changes apply to every welcome email sent from now on. Basic HTML like &lt;strong&gt; and links is allowed.
          </DialogDescription>
        </DialogHeader>
        {!tpl ? (
          <p className="text-sm text-[#5C5C5C] py-6 text-center">Loading…</p>
        ) : (
          <div className="space-y-4 py-1">
            {FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`wt-${f.key}`}>{f.label}</Label>
                {f.type === 'textarea' ? (
                  <Textarea
                    id={`wt-${f.key}`}
                    rows={f.rows}
                    value={tpl[f.key] || ''}
                    onChange={(e) => setTpl({ ...tpl, [f.key]: e.target.value })}
                    data-testid={`welcome-tpl-${f.key}`}
                  />
                ) : (
                  <Input
                    id={`wt-${f.key}`}
                    value={tpl[f.key] || ''}
                    onChange={(e) => setTpl({ ...tpl, [f.key]: e.target.value })}
                    data-testid={`welcome-tpl-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="outline"
            onClick={() => window.open(`${API}/emails/welcome-preview?name=Taylor`, '_blank', 'noopener')}
            data-testid="welcome-tpl-preview-btn"
          >
            <ExternalLink className="w-4 h-4 mr-1.5" />
            Preview saved letter
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !tpl} className="bg-[#34657f]" data-testid="welcome-tpl-save-btn">
              {saving ? 'Saving…' : 'Save letter'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeLetterEditor;
